import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "../site/SectionLabel";
import { HOME_SECTIONS } from "@/constants/testIds";
import { PROJECTS } from "@/lib/data";
import { useWorks } from "@/lib/works";

const ease = [0.76, 0, 0.24, 1];

export default function SelectedWork() {
  const works = useWorks("?homepage=true");
  const items = works && works.length ? works : PROJECTS;
  return (
    <section
      data-testid={HOME_SECTIONS.work}
      className="relative bg-ink-950 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel number="04">Selected Work</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-5xl leading-[0.98] tracking-tighter text-ivory lg:text-7xl">
              Six projects. Six problems.<br />
              <span className="italic text-gold">One point of view.</span>
            </h2>
          </div>
          <Link
            to="/work"
            className="group inline-flex items-center gap-3 self-start font-sans text-[12px] uppercase tracking-widest text-gold hover:text-ivory transition-colors md:self-end"
          >
            View all work
            <span className="h-px w-8 bg-current transition-all group-hover:w-12" />
          </Link>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:mt-24 lg:gap-x-12 lg:gap-y-24">
          {items.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease }}
              className={i % 2 === 1 ? "md:mt-24" : ""}
            >
              <Link
                to={p.href || `/work/${p.slug}`}
                className="group block"
                data-cursor="hover"
              >
                <div className="relative aspect-[4/5] overflow-hidden border border-ivory/[0.06] bg-ink-800">
                  <motion.img
                    src={p.thumb}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale-[35%] transition-all duration-[900ms] group-hover:grayscale-0 group-hover:scale-[1.03]"
                  />
                  {/* Spotlight overlay */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent transition-opacity duration-500 group-hover:opacity-40" />
                  <div className="absolute left-4 top-4 flex items-center gap-2 font-sans text-[10px] uppercase tracking-widest text-gold">
                    <span className="h-px w-4 bg-gold" />
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute right-4 top-4 rounded-full border border-ivory/20 bg-ink-950/60 px-3 py-1 font-sans text-[10px] uppercase tracking-widest text-ivory backdrop-blur">
                    {p.tag}
                  </div>
                  <div className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-ink-950/60 text-gold backdrop-blur transition-all group-hover:bg-gold group-hover:text-ink-950">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
                <div className="mt-6 flex items-start justify-between gap-6">
                  <div>
                    <h3 className="font-display text-3xl leading-tight tracking-tight text-ivory lg:text-4xl">
                      {p.title}
                    </h3>
                    <div className="mt-2 font-sans text-sm text-ivory-muted">
                      {p.client} · {p.industry} · {p.location}
                    </div>
                  </div>
                  <div className="shrink-0 font-sans text-[11px] uppercase tracking-widest text-ivory-dim">
                    {p.year}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
